import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { HilPresentationRequestEnvelope } from '@bizfirst/hil-types';

export interface HilStoreState {
  active: HilPresentationRequestEnvelope | null;
  pending: HilPresentationRequestEnvelope[];
  overlayOpen: boolean;
  submitting: boolean;
  submitError: string | null;
  formCache: Map<number, unknown>;

  _pushEvent: (envelope: HilPresentationRequestEnvelope) => void;
  // correlationID is optional so every existing real-EngageSession caller (Form/Approval, where
  // EngageSessionID alone is already a unique identity) keeps working unchanged. It is required
  // in practice for the no-Engage chat path — see the isMatch comment inside the implementation
  // below for why EngageSessionID alone is not safe to match on there.
  _resolveEvent: (engageSessionID: number, correlationID?: string) => void;
  _clearStaleResolved: (engageSessionID: number, staleRef: HilPresentationRequestEnvelope) => void;
  setSubmitting: (v: boolean) => void;
  setSubmitError: (msg: string | null) => void;
  setFormCache: (formID: number, schema: unknown) => void;
  closeOverlay: () => void;
  openOverlay: () => void;
}

// A re-suspending HIL session (e.g. Octopus AI Agent chat's Phase-2 continuing branch —
// see AiAgentNodeExecutor.ChatMessage.cs) pushes a follow-up SignalR envelope for the SAME
// EngageSessionID shortly after the browser's POST /api/hil/respond/{id} for the CURRENT
// turn resolves. Those two signals travel over independent transports (HTTP response vs
// SignalR), so their arrival order at the browser is not guaranteed. If the HTTP response
// wins, `_resolveEvent` would otherwise null `active` immediately — every consumer (e.g.
// HilObservePanel) renders purely off `!active` and unmounts HilChatWindow in that gap,
// which wipes its per-session transcript buffers (echoed/systemHistory); a later push then
// remounts a FRESH HilChatWindow that has lost every prior turn. See `_resolveEvent` below.
//
// NOT same-request network jitter: BaseHilRespondController.Respond awaits
// HilReplyContinuationService.ContinueAsync -> OrchestrationProcessor.ExecuteContinuationAsync
// synchronously end-to-end, so for Octopus AI Agent chat (no-Engage suspend/resume — see
// OctopusAiAgentNodeExecutorSettings) the browser's POST /api/hil/respond/0 does not resolve
// until the ENTIRE Phase-2 turn (LLM call, tool calls, re-suspend) has finished — the SignalR
// revival push and the HTTP response effectively race to arrive at roughly the same moment,
// not milliseconds apart. Live E2E measured real gaps of 24-61s for a single turn (bridge call
// + one tool call); a heavier multi-tool turn can run longer still. A short grace window here
// doesn't just risk losing history on rare jitter — with a real LLM in the loop it reliably
// fires well before the genuine revival arrives on every turn, flipping the panel to "No
// active HIL event" mid-conversation even though the workflow is still processing correctly.
// Sized generously above the measured worst case (61s) rather than tightly to it, while still
// bounded (not infinite) so a genuinely abandoned one-shot session eventually reverts to
// empty/next-pending instead of holding its answered envelope forever.
// Exported so tests exercise the real value rather than a duplicated magic number that
// silently drifts out of sync with it (see hilStore.test.ts).
export const CHAT_RESOLVE_GRACE_MS = 180_000;

export const useHilStore: UseBoundStore<StoreApi<HilStoreState>> = create<HilStoreState>()(
  immer((set, get) => ({
    active: null,
    pending: [],
    overlayOpen: false,
    submitting: false,
    submitError: null,
    formCache: new Map(),

    _pushEvent: (envelope) =>
      set((state) => {
        if (state.active?.Session.EngageSessionID === envelope.Session.EngageSessionID) {
          // Same-session revival — including one that arrives while the just-answered
          // envelope is being held during _resolveEvent's grace window below. Replace in
          // place so the ALREADY-MOUNTED HilChatWindow instance keeps rendering
          // continuously; queuing it into `pending` instead would strand it there forever,
          // since the resolve path only ever matches a session against `state.active`, never
          // `pending`.
          console.log('[HIL] Same-session revival — replacing active in place:', envelope.HilType, envelope.Session.EngageSessionID);
          state.active = envelope;
          state.overlayOpen = true;
        } else if (!state.active) {
          console.log('[HIL] No active envelope — setting as active and opening overlay:', envelope.HilType, envelope.Session.EngageSessionID);
          state.active = envelope;
          state.overlayOpen = true;
        } else {
          console.log('[HIL] Another envelope already active — queuing as pending:', envelope.HilType, envelope.Session.EngageSessionID, `(pending count now ${state.pending.length + 1})`);
          state.pending.push(envelope);
        }
      }),

    _resolveEvent: (engageSessionID, correlationID) => {
      // Read via `get()` (not inside the `set` producer below) so `current` is the real,
      // already-committed envelope object — not an Immer draft proxy that gets revoked once
      // its producer returns, which would make the reference-equality check in
      // `_clearStaleResolved` unreliable once captured into the timer's closure below.
      const current = get().active;

      // EngageSessionID alone is NOT a safe match key here: the no-Engage resume path (e.g.
      // Octopus AI Agent chat — see CorrelationID comment on HilSessionContext) always carries
      // EngageSessionID === 0 for EVERY turn of the same ongoing conversation, so it cannot tell
      // "the turn this resolve was actually for" apart from "whatever turn happens to be active
      // right now". Concretely: user replies to turn N -> POST /api/hil/respond/0 is in flight
      // (can legitimately take the full 24-61s+ Phase-2 turn per CHAT_RESOLVE_GRACE_MS above) ->
      // a SignalR push for turn N+1 (a brand-new, genuinely unanswered question) lands first and
      // replaces `active` via the same-session-revival branch in `_pushEvent` -> the POST for
      // turn N finally resolves and calls this function with engageSessionID=0. Matching on ID
      // alone would treat turn N+1 — which the user has NOT answered yet — as "just answered,
      // awaiting revival", starting a CHAT_RESOLVE_GRACE_MS countdown that silently clears a
      // still-active session out from under the user (observed as "No active HIL event" in
      // HilObservePanel even though the workflow is genuinely still suspended). CorrelationID is
      // unique per presentation turn (unlike EngageSessionID for this path), so require it to
      // match too whenever the caller has one available. Left optional (see interface above) so
      // real-Engage callers (Form/Approval — unique EngageSessionID per session, no collision
      // possible) and any caller that genuinely can't supply one keep their prior ID-only
      // matching behavior.
      const isMatch =
        current?.Session.EngageSessionID === engageSessionID &&
        (correlationID === undefined || current?.Session.CorrelationID === correlationID);

      if (!isMatch) {
        set((state) => {
          state.pending = state.pending.filter(
            (e) =>
              !(
                e.Session.EngageSessionID === engageSessionID &&
                (correlationID === undefined || e.Session.CorrelationID === correlationID)
              )
          );
          state.submitting = false;
          state.submitError = null;
        });
        return;
      }

      if (get().pending.length > 0) {
        set((state) => {
          state.active = state.pending.shift() ?? null;
          state.overlayOpen = state.active !== null;
          state.submitting = false;
          state.submitError = null;
        });
        return;
      }

      if (current!.HilType.toLowerCase() === 'chat') {
        // Hold the just-answered envelope in place instead of nulling it — see
        // CHAT_RESOLVE_GRACE_MS above for why. `_pushEvent` replaces `active` in place (by
        // reference) if a same-session revival lands first; the reference check below then
        // correctly no-ops instead of clearing a session that's already moved on.
        set((state) => {
          state.submitting = false;
          state.submitError = null;
        });
        const staleRef = current!;
        setTimeout(() => get()._clearStaleResolved(engageSessionID, staleRef), CHAT_RESOLVE_GRACE_MS);
        return;
      }

      set((state) => {
        state.active = null;
        state.overlayOpen = false;
        state.submitting = false;
        state.submitError = null;
      });
    },

    _clearStaleResolved: (engageSessionID, staleRef) => {
      // Reference-equality guard, not a session-ID check: a revival push for this same
      // session ID replaces `active` in place (see `_pushEvent`), so if nothing revived it
      // this is still the exact object we scheduled the check for; if something did,
      // `active` is now a different object and this is correctly a no-op.
      //
      // This comparison MUST happen via `get()` outside any `set`/immer producer. Inside a
      // producer, `state.active` is a fresh draft Proxy wrapping the raw object (even when
      // the producer only mutates a sibling field) — it is never reference-equal (===) to
      // `staleRef`, which was captured as a plain (non-draft) object in `_resolveEvent`. That
      // made the guard permanently false and left `_clearStaleResolved` unable to ever clear
      // a stale session — see DevelopmentHistoryLog.md.
      if (get().active !== staleRef) {
        return;
      }
      console.log('[HIL] Resolved chat session did not revive within grace window — clearing:', engageSessionID);
      set((state) => {
        state.active = null;
        state.overlayOpen = false;
      });
    },

    setSubmitting: (v) =>
      set((state) => {
        state.submitting = v;
      }),

    setSubmitError: (msg) =>
      set((state) => {
        state.submitError = msg;
        state.submitting = false;
      }),

    setFormCache: (formID, schema) =>
      set((state) => {
        state.formCache.set(formID, schema);
      }),

    closeOverlay: () =>
      set((state) => {
        state.overlayOpen = false;
      }),

    openOverlay: () =>
      set((state) => {
        if (state.active) state.overlayOpen = true;
      }),
  }))
);
